import { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import './Navbar.css';

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const learnRef = useRef(null);

  // Shadow on scroll
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock page scroll while the mobile drawer is open
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  // Close Learn dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (learnRef.current && !learnRef.current.contains(e.target)) {
        setLearnOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
    {learnOpen && (
      <div className="learn-backdrop" onClick={() => setLearnOpen(false)} />
    )}
    <header className="navbar">
      <div className={`navbar-inner${scrolled ? ' navbar-inner--scrolled' : ''}`}>
        <div className="navbar-container">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img src="/favicon.svg" alt="HorseStat" className="navbar-favicon" />
            <span className="logo-horse">HORSE</span>
            <span className="logo-stat">STAT</span>
          </Link>

          {/* Center nav */}
          <nav className="navbar-links">
            <NavLink to="/features" className="pill-item" onClick={() => setMenuOpen(false)}>
              Features
            </NavLink>

            {/* Learn dropdown */}
            <div className="learn-dropdown" ref={learnRef}>
              <button
                className={`pill-item learn-btn${learnOpen ? ' is-active' : ''}`}
                onClick={() => setLearnOpen((v) => !v)}
                aria-expanded={learnOpen}
              >
                Learn
                <svg
                  className="learn-chevron"
                  style={{ transform: learnOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Always in the DOM (crawlable); shown/hidden with the `hidden` attribute. */}
              <div className="learn-panel" hidden={!learnOpen}>
                  <Link
                    to="/getting-started"
                    className="learn-panel-item"
                    onClick={() => setLearnOpen(false)}
                  >
                    <span className="learn-panel-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
                      </svg>
                    </span>
                    <div>
                      <span className="learn-panel-title">Getting Started</span>
                      <span className="learn-panel-desc">Watch our platform in action</span>
                    </div>
                    <svg className="learn-panel-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                  <Link
                    to="/help"
                    className="learn-panel-item"
                    onClick={() => setLearnOpen(false)}
                  >
                    <span className="learn-panel-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <path d="M12 17h.01" />
                      </svg>
                    </span>
                    <div>
                      <span className="learn-panel-title">Help Centre</span>
                      <span className="learn-panel-desc">Get answers and support</span>
                    </div>
                    <svg className="learn-panel-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                </div>
            </div>
          </nav>

          {/* Right actions */}
          <div className="navbar-actions">
            <NavLink to="/pricing" className="pill-item">Pricing</NavLink>
            <a
              href="https://app.horsestat.com"
              className="nav-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to app
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`nav-hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="mobile-drawer-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      <div className={`mobile-drawer ${menuOpen ? 'open' : ''}`} aria-hidden={!menuOpen}>
        <div className="mobile-drawer-inner">
          <div className="mobile-drawer-header">
            <Link to="/" className="mobile-drawer-brand" onClick={() => setMenuOpen(false)}>
              <span className="logo-horse">HORSE</span>
              <span className="logo-stat">STAT</span>
            </Link>
            <button
              type="button"
              className="mobile-drawer-close"
              onClick={() => setMenuOpen(false)}
              aria-label="Close navigation"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <NavLink to="/features" className="mobile-link" onClick={() => setMenuOpen(false)}>Features</NavLink>
          <NavLink to="/pricing" className="mobile-link" onClick={() => setMenuOpen(false)}>Pricing</NavLink>

          <span className="mobile-section-label">Learn</span>
          <Link to="/getting-started" className="mobile-link mobile-link--sub" onClick={() => setMenuOpen(false)}>
            <span className="mobile-link-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
              </svg>
            </span>
            Getting Started
          </Link>
          <Link to="/help" className="mobile-link mobile-link--sub" onClick={() => setMenuOpen(false)}>
            <span className="mobile-link-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
              </svg>
            </span>
            Help Centre
          </Link>

          <a
            href="https://app.horsestat.com"
            className="mobile-cta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
          >
            Open web app
          </a>
        </div>
      </div>
    </header>
    </>
  );
}

export default Navbar;
